import React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FC, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';

import { PROJECT_NOTE_MAX_LENGTH as NOTE_MAX_LENGTH } from '../../../entities/project/api/projectApi';
import type { ProjectNoteController } from '../../../entities/project/model/useProjectNote';
import { cx } from '../../../utils/cx';
import { formatDateTime } from '../../../utils/format';
import styles from './ProjectNotesWindow.module.scss';

interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

type Edge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const EDGES: Edge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const MIN_W = 320;
const MIN_H = 220;
const MARGIN = 8;
const STORAGE_KEY = 'projectNotes.windowRect';

/** По умолчанию — 2/3 экрана по центру */
const defaultRect = (): Rect => {
    const w = Math.round(window.innerWidth * (2 / 3));
    const h = Math.round(window.innerHeight * (2 / 3));
    return { x: Math.round((window.innerWidth - w) / 2), y: Math.round((window.innerHeight - h) / 2), w, h };
};

/** Не даём окну вылезти за экран (например, после ресайза браузера) */
const clampRect = (rect: Rect): Rect => {
    const maxW = window.innerWidth - MARGIN * 2;
    const maxH = window.innerHeight - MARGIN * 2;
    const w = Math.min(Math.max(rect.w, MIN_W), maxW);
    const h = Math.min(Math.max(rect.h, MIN_H), maxH);
    const x = Math.min(Math.max(rect.x, MARGIN), window.innerWidth - w - MARGIN);
    const y = Math.min(Math.max(rect.y, MARGIN), window.innerHeight - h - MARGIN);
    return { x, y, w, h };
};

const readStoredRect = (): Rect | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<Rect>;
        if ([parsed.x, parsed.y, parsed.w, parsed.h].every((v) => typeof v === 'number')) return parsed as Rect;
    } catch {
        /* storage недоступен — используем дефолт */
    }
    return null;
};

const storeRect = (rect: Rect) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(rect));
    } catch {
        /* ignore */
    }
};

interface ProjectNotesWindowProps {
    open: boolean;
    projectTitle: string;
    note: ProjectNoteController;
    onClose: () => void;
}

export const ProjectNotesWindow: FC<ProjectNotesWindowProps> = ({ open, projectTitle, note, onClose }) => {
    const [rect, setRect] = useState<Rect>(() => clampRect(readStoredRect() ?? defaultRect()));
    const [maximized, setMaximized] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const rectRef = useRef(rect);
    rectRef.current = rect;

    // Фокус в поле при открытии
    useLayoutEffect(() => {
        if (!open) return;
        setRect((r) => clampRect(r));
        const id = window.requestAnimationFrame(() => textareaRef.current?.focus());
        return () => window.cancelAnimationFrame(id);
    }, [open]);

    // Держим окно в пределах экрана при ресайзе браузера
    useEffect(() => {
        if (!open) return undefined;
        const onResize = () => setRect((r) => clampRect(r));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [open]);

    const close = useCallback(() => {
        void note.flush();
        onClose();
    }, [note, onClose]);

    /** Общий обработчик drag / resize через pointer events */
    const startGesture = (event: ReactPointerEvent, apply: (start: Rect, dx: number, dy: number) => Rect) => {
        if (event.button !== 0 || maximized) return;
        event.preventDefault();
        const start = rectRef.current;
        const sx = event.clientX;
        const sy = event.clientY;
        document.body.classList.add(styles.noSelect);

        const onMove = (e: PointerEvent) => setRect(clampRect(apply(start, e.clientX - sx, e.clientY - sy)));
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
            document.body.classList.remove(styles.noSelect);
            storeRect(rectRef.current);
        };

        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
    };

    const onDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest('button')) return;
        startGesture(event, (s, dx, dy) => ({ ...s, x: s.x + dx, y: s.y + dy }));
    };

    const onResizeStart = (edge: Edge) => (event: ReactPointerEvent<HTMLDivElement>) => {
        startGesture(event, (s, dx, dy) => {
            let { x, y, w, h } = s;
            if (edge.includes('e')) w = Math.max(MIN_W, s.w + dx);
            if (edge.includes('s')) h = Math.max(MIN_H, s.h + dy);
            if (edge.includes('w')) {
                w = Math.max(MIN_W, s.w - dx);
                x = s.x + (s.w - w);
            }
            if (edge.includes('n')) {
                h = Math.max(MIN_H, s.h - dy);
                y = s.y + (s.h - h);
            }
            return { x, y, w, h };
        });
    };

    const resetSize = () => {
        const next = defaultRect();
        setMaximized(false);
        setRect(next);
        storeRect(next);
    };

    const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
            event.preventDefault();
            void note.flush();
        }
    };

    if (!open) return null;

    const length = note.text.length;
    const nearLimit = length > NOTE_MAX_LENGTH * 0.9;

    const statusText = (() => {
        if (note.loading) return 'Загрузка…';
        switch (note.saveState) {
            case 'dirty':
                return 'Есть несохранённые изменения';
            case 'saving':
                return 'Сохранение…';
            case 'error':
                return note.saveError ?? 'Не удалось сохранить';
            case 'saved':
                return note.note ? `Сохранено ${formatDateTime(new Date(note.note.updated_at))}` : 'Сохранено';
            default:
                return note.note ? `Изменено ${formatDateTime(new Date(note.note.updated_at))}` : 'Заметки пока нет';
        }
    })();

    const style = maximized
        ? { left: MARGIN, top: MARGIN, width: `calc(100vw - ${MARGIN * 2}px)`, height: `calc(100vh - ${MARGIN * 2}px)` }
        : { left: rect.x, top: rect.y, width: rect.w, height: rect.h };

    return createPortal(
        <div
            className={cx(styles.window, maximized && styles.maximized)}
            style={style}
            role="dialog"
            aria-label={`Заметки к проекту «${projectTitle}»`}
            onKeyDown={onKeyDown}
        >
            <div className={styles.titleBar} onPointerDown={onDragStart} onDoubleClick={() => setMaximized((m) => !m)}>
                <span className={styles.title}>
                    Заметки <span className={styles.project}>· {projectTitle}</span>
                </span>
                <div className={styles.controls}>
                    <button type="button" title="Исходный размер (2/3 экрана)" aria-label="Исходный размер" onClick={resetSize}>
                        <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3.5" y="4.5" width="9" height="7" rx="1" /></svg>
                    </button>
                    <button
                        type="button"
                        title={maximized ? 'Свернуть в окно' : 'Развернуть'}
                        aria-label={maximized ? 'Свернуть в окно' : 'Развернуть'}
                        onClick={() => setMaximized((m) => !m)}
                    >
                        {maximized ? (
                            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2.5v3.5H2.5M10 2.5v3.5h3.5M6 13.5V10H2.5M10 13.5V10h3.5" /></svg>
                        ) : (
                            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 6V2.5H6M13.5 6V2.5H10M2.5 10v3.5H6M13.5 10v3.5H10" /></svg>
                        )}
                    </button>
                    <button type="button" title="Закрыть (Esc)" aria-label="Закрыть" onClick={close}>
                        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                    </button>
                </div>
            </div>

            <div className={styles.body}>
                {note.loadError ? (
                    <div className={styles.loadError}>
                        <p>{note.loadError}</p>
                        <button type="button" onClick={() => void note.reload()}>
                            Повторить
                        </button>
                    </div>
                ) : (
                    <textarea
                        ref={textareaRef}
                        className={styles.textarea}
                        value={note.text}
                        maxLength={NOTE_MAX_LENGTH}
                        disabled={note.loading}
                        placeholder={note.loading ? '' : 'Пишите здесь — заметка сохраняется автоматически'}
                        spellCheck
                        aria-label="Текст заметки"
                        onChange={(event) => note.setText(event.target.value)}
                        onBlur={() => void note.flush()}
                    />
                )}
            </div>

            <div className={styles.footer}>
                <span className={cx(styles.status, note.saveState === 'error' && styles.statusError)} role="status">
                    {statusText}
                    {note.saveState === 'error' && (
                        <button type="button" className={styles.retry} onClick={() => void note.flush()}>
                            Повторить
                        </button>
                    )}
                </span>
                <span className={cx(styles.counter, nearLimit && styles.counterWarn)}>
                    {length.toLocaleString('ru-RU')} / {NOTE_MAX_LENGTH.toLocaleString('ru-RU')}
                </span>
            </div>

            {!maximized &&
                EDGES.map((edge) => (
                    <div key={edge} className={cx(styles.handle, styles[`handle_${edge}`])} onPointerDown={onResizeStart(edge)} />
                ))}
        </div>,
        document.body,
    );
};